import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { addDays, format, isPast, startOfWeek, subDays } from "date-fns";
import { useRouter } from "expo-router";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, Text, View } from "react-native";
import LeftArrowIcon from "@/assets/svgs/left-arrow-icon";
import RightArrowIcon from "@/assets/svgs/right-arrow-icon";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { api } from "@/elements/components/utils/convex-api";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import ParentChildren from "../lesson-history/parent-children";
import ParentLessonHistoryHome from "../lesson-history/parent-lesson-history-home";
import ParentTutors from "../lesson-history/parent-tutors";
import ChildrenTabList from "../shared/children-tab-list";
import ParentBookingCard from "../shared/parent-booking-card";
import ParentSessionCard from "../shared/parent-session-card";
import { useSheet } from "../shared/sheet-provider";
import TutorCard from "../shared/tutor-card";
import type {
  ChildrenSelectSheetPayload,
  ChildrenSelectSheetResult,
} from "../sheets/children-select-sheet";
import { SheetIds } from "../utils/sheet-ids";

type PendingBooking = NonNullable<
  typeof api.parent.home.queries.pendingBookings._returnType
>[0];

type GroupedBooking = {
  bookings: PendingBooking[];
  isRecurring: boolean;
  firstBookingId: Id<"bookings">;
};

const getFirstDayOfWeek = (date: Date) => {
  const firstDay = startOfWeek(date, { weekStartsOn: 1 });
  return firstDay;
};

function ParentHome() {
  const { data: currentUser } = useQuery(
    convexQuery(api.account.queries.getCurrentUser, {}),
  );
  const name = currentUser?.name;
  const initialWeekStartDate = getFirstDayOfWeek(new Date());
  const [weekStartDate, setWeekStartDate] = useState(initialWeekStartDate);
  const [selectedChildId, setSelectedChildId] = useState<Id<"students"> | null>(
    null,
  );
  const currentMonth = format(weekStartDate, "MMMM");
  const weekEndDate = addDays(weekStartDate, 6);
  const { data: childrenData } = useQuery(
    convexQuery(api.parent.home.queries.children, {}),
  );
  const children = childrenData ?? [];
  const [firstChild] = children;
  const firstChildId = firstChild?._id;
  const childrenIds = children.map((child) => child._id);
  const childrenNames = children.map((child) => child.name);
  const responsiveWidth = useResponsiveWidth();
  const tutorColumns = responsiveWidth < 500 ? 2 : 4;
  const tutorCardWidth =
    (responsiveWidth - 32 - (tutorColumns - 1) * 16) / tutorColumns;

  const { open } = useSheet<
    ChildrenSelectSheetPayload,
    ChildrenSelectSheetResult
  >(SheetIds.CHILDREN_SELECT);
  const router = useRouter();
  // Convert local dates to UTC dates with same calendar date
  const weekStartDateUTC = new Date(
    Date.UTC(
      weekStartDate.getFullYear(),
      weekStartDate.getMonth(),
      weekStartDate.getDate(),
    ),
  );
  const weekEndDateUTC = new Date(
    Date.UTC(
      weekEndDate.getFullYear(),
      weekEndDate.getMonth(),
      weekEndDate.getDate(),
      23,
      59,
      59,
      999,
    ),
  );

  const { data: weeklySessionsData } = useQuery(
    convexQuery(
      api.parent.home.queries.weeklySessions,
      selectedChildId
        ? {
            studentId: selectedChildId as Id<"students">,
            startTime: weekStartDateUTC.getTime(),
            endTime: weekEndDateUTC.getTime(),
          }
        : "skip",
    ),
  );
  const weeklySessions = weeklySessionsData ?? [];
  const { data: pendingBookingsData } = useQuery(
    convexQuery(
      api.parent.home.queries.pendingBookings,
      selectedChildId
        ? {
            studentId: selectedChildId as Id<"students">,
          }
        : "skip",
    ),
  );
  const pendingBookings = pendingBookingsData ?? [];
  const {
    mutateAsync: cancelBookingRequest,
    isPending: isCancellingBookingRequest,
  } = useMutation({
    mutationFn: useConvexMutation(api.parent.home.mutations.cancelBooking),
  });

  const groupedBookings = useMemo(() => {
    const groups: GroupedBooking[] = [];
    const recurringGroups = new Map<string, PendingBooking[]>();
    const standaloneBookings: PendingBooking[] = [];

    for (const booking of pendingBookings) {
      if (booking.recurringGroupId) {
        const existing = recurringGroups.get(booking.recurringGroupId) || [];
        existing.push(booking);
        recurringGroups.set(booking.recurringGroupId, existing);
      } else {
        standaloneBookings.push(booking);
      }
    }

    for (const [, bookings] of recurringGroups) {
      const sorted = bookings.sort((a, b) => a.startTime - b.startTime);
      groups.push({
        bookings: sorted,
        isRecurring: true,
        firstBookingId: sorted[0]!._id,
      });
    }

    for (const booking of standaloneBookings) {
      groups.push({
        bookings: [booking],
        isRecurring: false,
        firstBookingId: booking._id,
      });
    }

    return groups.sort(
      (a, b) => a.bookings[0]!.startTime - b.bookings[0]!.startTime,
    );
  }, [pendingBookings]);

  const { data: recommendedTutorsData } = useQuery(
    convexQuery(api.parent.home.queries.recommendedTutors, {
      childrenIds,
    }),
  );
  const recommendedTutors = recommendedTutorsData ?? [];

  const weeklySessionsCount = weeklySessions.length;

  useEffect(() => {
    if (firstChildId && !selectedChildId) {
      setSelectedChildId(firstChildId);
    }
  }, [firstChildId, selectedChildId]);

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <ResponsiveContent>
        <View className="h-3" />
        <View className="mx-4">
          <View className="gap-1">
            <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[32px] text-gray-800">
              Hi {name}
            </Text>
            <Text className="font-geologica-regular font-normal text-[20px] text-gray-800 leading-[1.5]">
              {`Here's your children's learning plan at a glance.`}
            </Text>
          </View>
          <View className="h-8" />

          {recommendedTutors.length === 0 ? (
            <Fragment>
              <View className="flex-row items-start justify-between">
                <View className="gap-2">
                  <Text className="font-geologica-medium font-medium text-[20px] text-gray-800 leading-[1.5]">
                    Weekly schedule
                  </Text>

                  <Text className="font-geologica-regular font-normal text-[16px] text-gray-800 leading-[1.5]">
                    {format(weekStartDate, "do")} - {format(weekEndDate, "do")}{" "}
                    {currentMonth}
                  </Text>
                </View>

                <View className="gap-2 items-center">
                  <View className="flex-row items-center gap-6 self-end">
                    <Pressable
                      className="disabled:opacity-50"
                      disabled={isPast(weekStartDate)}
                      onPress={() =>
                        setWeekStartDate(subDays(weekStartDate, 7))
                      }
                    >
                      <LeftArrowIcon
                        opacity={isPast(weekStartDate) ? 0.5 : 1}
                      />
                    </Pressable>

                    <RightArrowIcon
                      onPress={() =>
                        setWeekStartDate(addDays(weekStartDate, 7))
                      }
                    />
                  </View>

                  <View className="px-2 py-1 bg-teachLoop-creamDark rounded-md">
                    <Text className="font-geologica-medium font-medium text-xs text-teachLoop-textPrimary leading-[1.5] uppercase">
                      {weeklySessionsCount} lessons left
                    </Text>
                  </View>
                </View>
              </View>
              <View className="h-6" />
              <ChildrenTabList
                childList={children}
                selectedChildId={selectedChildId}
                setSelectedChildId={setSelectedChildId}
              />
              <View className="h-6" />
              <View className="gap-4">
                {weeklySessions.length > 0 ? (
                  weeklySessions
                    .sort((a, b) => a.startTime - b.startTime)
                    .map((session) => {
                      return (
                        <ParentSessionCard
                          key={session._id}
                          session={session}
                        />
                      );
                    })
                ) : (
                  <Text className="font-geologica-regular text-base text-gray-800 leading-[1.5]">
                    You have no lessons booked this week
                  </Text>
                )}

                {groupedBookings.length > 0 && (
                  <Fragment>
                    <Text className="font-geologica-medium font-medium text-[16px] text-gray-800 leading-[1.2]">
                      {groupedBookings.length} pending request
                      {groupedBookings.length > 1 ? "s" : ""} sent
                    </Text>
                    {groupedBookings.map((group) => {
                      return (
                        <ParentBookingCard
                          bookings={group.bookings}
                          isRecurring={group.isRecurring}
                          key={group.firstBookingId}
                          onCancelRequest={() => {
                            cancelBookingRequest({
                              bookingId: group.firstBookingId,
                            });
                          }}
                          isCancellingBookingRequest={
                            isCancellingBookingRequest
                          }
                        />
                      );
                    })}
                  </Fragment>
                )}
              </View>
              <View className="h-6" />
            </Fragment>
          ) : (
            <Fragment>
              <View className="px-4 py-10 bg-teachLoop-creamDark rounded-2xl">
                <Text className="font-geologica-regular text-center text-base text-gray-800 leading-[1.5]">
                  You have no lessons booked yet.
                </Text>
              </View>

              <View className="h-8" />

              <View className="gap-6">
                <Text className="font-geologica-medium font-medium text-xl text-black leading-[1.5]">
                  Recommended tutors for {childrenNames.join(", ")}
                </Text>

                <View className="flex-row gap-4 flex-wrap justify-evenly items-start">
                  {recommendedTutors.map((tutor) => (
                    <View
                      key={tutor!._id}
                      className="items-center"
                      style={{ width: tutorCardWidth }}
                    >
                      <TutorCard
                        tutor={tutor}
                        onPress={async () => {
                          const matchingStudents = [
                            ...(tutor?.matchingStudents ?? []),
                          ];

                          const [preSelectedStudent] = matchingStudents;
                          if (matchingStudents.length > 1) {
                            const result = await open({
                              title: "Booking lessons for",
                              options: matchingStudents.map((student) => ({
                                label: student.name,
                                value: student._id,
                                imageUrl: student.imageUrl ?? null,
                              })),
                              preSelectedOption: preSelectedStudent._id,
                            });
                            if (result.selectedOption) {
                              router.push(
                                `/(app)/home/find-tutor/${tutor!._id}?childId=${result.selectedOption}`,
                              );
                            }
                          } else {
                            router.push(
                              `/(app)/home/find-tutor/${tutor!._id}?childId=${preSelectedStudent._id}`,
                            );
                          }
                        }}
                      />
                    </View>
                  ))}
                </View>
              </View>
            </Fragment>
          )}

          <ParentLessonHistoryHome />
          <ParentChildren />
          <ParentTutors />
          <View className="h-8" />
        </View>
      </ResponsiveContent>
    </ScrollView>
  );
}

export default ParentHome;
